import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../api/axiosInstance";

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
}

// Quick-nav buttons shown in the action bar
const NAV_ITEMS = [
  { label: 'Payment Method',    icon: 'ti-credit-card',  path: '/Admin/PaymentMethod' },
  { label: 'Currency',          icon: 'ti-coin',         path: '/Admin/Currency'      },
  { label: 'Terms & Conditions',icon: 'ti-file-text',    path: '/Admin/TermCondition' },
  { label: 'Receipt Template',  icon: 'ti-receipt',      path: null },
  { label: 'Discount Type',     icon: 'ti-tag',          path: null },
  { label: 'Service List',      icon: 'ti-list',         path: null },
  { label: 'Invoice Template',  icon: 'ti-file-invoice', path: null },
]

const modalOverlay = {
  position: 'fixed', inset: 0, zIndex: 1050,
  background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const modalCard = {
  background: '#fff', borderRadius: 12, width: 360, padding: 24,
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
}
const modalHeader = (color) => ({
  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18,
})
const modalIconBox = (color) => ({
  width: 34, height: 34, borderRadius: 8,
  background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
})
const closeBtn = {
  marginLeft: 'auto', width: 26, height: 26, borderRadius: 6,
  border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14,
}
const modalInput = (accentColor) => ({
  flex: 1, height: 38, borderRadius: 8, border: '1.5px solid #dde3eb',
  padding: '0 12px', fontSize: 15, fontWeight: 600, color: '#1a202c',
  outline: 'none', textAlign: 'center',
})
const modalActions = {
  display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20,
}
const cancelBtn = {
  padding: '7px 16px', fontSize: 13, borderRadius: 8,
  border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
}

const FinanceSetting = () => {
  const navigate = useNavigate();

  // Discount modal
  const [discountOpen,  setDiscountOpen]  = useState(false);
  const [discountValue, setDiscountValue] = useState(0);
  const [isSaving,      setIsSaving]      = useState(false);
  const saveTimerRef = useRef(null);

  // VAT modal
  const [vatOpen,    setVatOpen]    = useState(false);
  const [vatValue,   setVatValue]   = useState(0);
  const [savingVat,  setSavingVat]  = useState(false);
  const vatSaveTimerRef = useRef(null);

  // Fetch VAT on mount
  useEffect(() => {
    const ctrl = new AbortController();
    axiosInstance.get('getAllVat', { signal: ctrl.signal })
      .then(res => {
        const d = res.data;
        const v = Array.isArray(d?.data) && d.data.length > 0 ? Number(d.data[0].vat_value) : Number(d?.vat_value ?? 0);
        setVatValue(Number.isFinite(v) ? v : 0);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  // Fetch discount when modal opens
  useEffect(() => {
    if (!discountOpen) return;
    const ctrl = new AbortController();
    axiosInstance.get('getAllDiscount', { signal: ctrl.signal })
      .then(res => {
        const d = res.data;
        const v = Array.isArray(d?.data) && d.data.length > 0 ? Number(d.data[0].discount_value) : 0;
        setDiscountValue(Number.isFinite(v) ? v : 0);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [discountOpen]);

  const persistDiscount = useCallback(async (value) => {
    setIsSaving(true);
    const ep = 'updateDiscount/1';
    const n  = Number(value);
    try {
      await axiosInstance.post(ep, { discount_value: n })
        .catch(() => axiosInstance.put(ep, { discount_value: n }));
    } finally { setIsSaving(false); }
  }, []);

  const handleSliderChange = (e) => {
    const v = Number(e.target.value);
    setDiscountValue(v);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persistDiscount(v), 400);
  };

  const persistVat = useCallback(async (value) => {
    const n  = Number(value);
    const ep = 'updateVat/1';
    await axiosInstance.put(ep, { vat_value: n })
      .catch(() => axiosInstance.post(ep, { vat_value: n }));
  }, []);

  const handleVatSave = async () => {
    setSavingVat(true);
    try {
      await persistVat(vatValue);
      Swal.fire({ icon: 'success', title: 'Saved', text: 'VAT updated successfully.', timer: 1500, showConfirmButton: false });
      setVatOpen(false);
    } catch {
      Swal.fire('Error', 'Failed to update VAT', 'error');
    } finally { setSavingVat(false); }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.green}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-coin" style={{ color: COLORS.green, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Finance Settings</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage VAT, discounts, currencies and payment options</div>
            </div>
          </div>

          {/* ── Action Bar ── */}
          <div style={{
            padding: '10px 20px', borderBottom: '1px solid #e8ecf0',
            background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            {/* Settings modals */}
            <button
              className="bgBtn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: '5px 12px' }}
              onClick={() => setVatOpen(true)}
            >
              <i className="ti ti-percentage" style={{ fontSize: 15 }} /> VAT
            </button>
            <button
              className="bgBtn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: '5px 12px' }}
              onClick={() => setDiscountOpen(true)}
            >
              <i className="ti ti-tag" style={{ fontSize: 15 }} /> Discount Limit
            </button>

            {/* Divider */}
            <div style={{ width: 1, height: 24, background: '#e8ecf0', margin: '0 4px' }} />

            {/* Navigation buttons */}
            {NAV_ITEMS.map(item => (
              <button
                key={item.label}
                onClick={() => item.path && navigate(item.path)}
                disabled={!item.path}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', fontSize: 13, borderRadius: 7,
                  border: '1px solid #dde3eb',
                  background: item.path ? '#fff' : '#f8f9fb',
                  color: item.path ? '#2d3748' : '#aaa',
                  cursor: item.path ? 'pointer' : 'default',
                }}
                onMouseEnter={e => { if (item.path) e.currentTarget.style.borderColor = COLORS.blue }}
                onMouseLeave={e => { if (item.path) e.currentTarget.style.borderColor = '#dde3eb' }}
              >
                <i className={`ti ${item.icon}`} style={{ fontSize: 14 }} />
                {item.label}
              </button>
            ))}
          </div>

          {/* ── Body: setting summary cards ── */}
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>

              {/* VAT card */}
              <div style={{
                flex: '1 1 220px', borderRadius: 10, border: '1px solid #e8ecf0',
                padding: '16px 20px', background: '#fafbfc',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: `${COLORS.green}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="ti ti-percentage" style={{ color: COLORS.green, fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>VAT</div>
                    <div style={{ fontSize: 11, color: COLORS.gray }}>Value Added Tax rate</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: COLORS.green }}>{vatValue}<span style={{ fontSize: 14, fontWeight: 600 }}>%</span></span>
                  <button
                    onClick={() => setVatOpen(true)}
                    style={{
                      width: 30, height: 30, borderRadius: 8, border: `1px solid ${COLORS.green}44`,
                      background: `${COLORS.green}12`, color: COLORS.green, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                    }}
                    title="Edit VAT"
                  >
                    <i className="ti ti-pencil" />
                  </button>
                </div>
              </div>

              {/* Discount Limit card */}
              <div style={{
                flex: '1 1 220px', borderRadius: 10, border: '1px solid #e8ecf0',
                padding: '16px 20px', background: '#fafbfc',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: `${COLORS.orange}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="ti ti-tag" style={{ color: COLORS.orange, fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>Discount Limit</div>
                    <div style={{ fontSize: 11, color: COLORS.gray }}>Maximum allowed discount</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: COLORS.orange }}>{discountValue}<span style={{ fontSize: 14, fontWeight: 600 }}>%</span></span>
                  <button
                    onClick={() => setDiscountOpen(true)}
                    style={{
                      width: 30, height: 30, borderRadius: 8, border: `1px solid ${COLORS.orange}44`,
                      background: `${COLORS.orange}12`, color: COLORS.orange, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                    }}
                    title="Edit Discount Limit"
                  >
                    <i className="ti ti-pencil" />
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* ── VAT Modal ── */}
        {vatOpen && (
          <div style={modalOverlay} onClick={() => setVatOpen(false)}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
              <div style={modalHeader(COLORS.green)}>
                <div style={modalIconBox(COLORS.green)}>
                  <i className="ti ti-percentage" style={{ color: COLORS.green, fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>VAT Rate</div>
                  <div style={{ fontSize: 11, color: COLORS.gray }}>Set the Value Added Tax percentage</div>
                </div>
                <button style={closeBtn} onClick={() => setVatOpen(false)}>
                  <i className="ti ti-x" />
                </button>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                  VAT percentage (%)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min={0} max={100}
                    value={vatValue}
                    onChange={e => {
                      const v = Math.max(0, Math.min(100, Number(e.target.value)));
                      setVatValue(v);
                      clearTimeout(vatSaveTimerRef.current);
                      vatSaveTimerRef.current = setTimeout(() => persistVat(v), 400);
                    }}
                    style={modalInput(COLORS.green)}
                    onFocus={e => e.target.style.borderColor = COLORS.green}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'}
                  />
                  <span style={{ fontSize: 13, color: COLORS.gray }}>%</span>
                </div>
              </div>

              <div style={modalActions}>
                <button style={cancelBtn} onClick={() => setVatOpen(false)}>Cancel</button>
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ padding: '7px 16px', fontSize: 13 }}
                  onClick={handleVatSave}
                  disabled={savingVat}
                >
                  {savingVat && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                  {savingVat ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Discount Limit Modal ── */}
        {discountOpen && (
          <div style={modalOverlay} onClick={() => setDiscountOpen(false)}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
              <div style={modalHeader(COLORS.orange)}>
                <div style={modalIconBox(COLORS.orange)}>
                  <i className="ti ti-tag" style={{ color: COLORS.orange, fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Discount Limit</div>
                  <div style={{ fontSize: 11, color: COLORS.gray }}>Maximum discount staff can apply</div>
                </div>
                <button style={closeBtn} onClick={() => setDiscountOpen(false)}>
                  <i className="ti ti-x" />
                </button>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray }}>Maximum discount</label>
                  <span style={{ fontSize: 20, fontWeight: 800, color: COLORS.orange }}>{discountValue}%</span>
                </div>
                <input
                  type="range" min={0} max={100} step={1}
                  value={discountValue}
                  onChange={handleSliderChange}
                  style={{ width: '100%', accentColor: COLORS.orange }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: COLORS.gray, marginTop: 4 }}>
                  <span>0%</span><span>50%</span><span>100%</span>
                </div>
                {isSaving && (
                  <div style={{ fontSize: 11, color: COLORS.orange, marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Saving…
                  </div>
                )}
              </div>

              <div style={modalActions}>
                <button style={cancelBtn} onClick={() => setDiscountOpen(false)}>Cancel</button>
                <button
                  className="bgBtn btn-sm"
                  style={{ padding: '7px 16px', fontSize: 13 }}
                  onClick={() => setDiscountOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FinanceSetting;
